import {
    CheckIcon,
    KeyboardArrowRightIcon,
    Stack,
    Tooltip,
    Typography,
    WarningIcon,
} from '@ds/coolshop';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { PimSideBarElement, PimSideBarElementActions } from '@src/components/layout/PimSideBar';
import { useAuth } from '@src/hooks';
import React from 'react';
import { useIntl } from 'react-intl';

import { ContentTypeGroupStatuses } from './ContentTypeGroupStatuses';
import useStyles from './styles';
import { EdgeSelectedFn, NodeEdge, NodeViewMode } from './types';

type SingleNodeViewProps = {
    edge: NodeEdge;
    onEdgeSelected: EdgeSelectedFn;
    selectedNodeId: string | undefined;
    showCtgStatus: boolean;
    mode: NodeViewMode;
    isInTranslation?: boolean;
    showChildren?: boolean;
    showBreadcrumb?: boolean;
    enablePublish: boolean;
    showPublishInfo: boolean;
    /** Optional leading icon, e.g. the MEDIA node kind. Other hierarchies pass nothing. */
    startIcon?: React.ReactNode;
    /** Optional second line under the title, e.g. a media node's human name. */
    subtitle?: string;
};

export const SingleNodeView = React.forwardRef<HTMLDivElement, SingleNodeViewProps>(
    (props, ref) => {
        const {
            edge,
            onEdgeSelected,
            mode,
            selectedNodeId,
            showCtgStatus,
            isInTranslation = false,
            showChildren = true,
            showBreadcrumb = false,
            enablePublish,
            showPublishInfo,
            startIcon,
            subtitle,
        } = props;

        const intl = useIntl();
        const { hasUserRole } = useAuth();
        const { nodeListEdgeBackground } = useStyles();

        const isHierarchyViewMode = mode === 'hierarchy-viewer';

        const { node } = edge;

        const userHasPermissions =
            mode === 'change-parent'
                ? !!edge.node.writeRoles?.some(role => hasUserRole(role))
                : true;

        const showContentTypeGroupsStatusesIcon =
            isHierarchyViewMode &&
            !showCtgStatus &&
            enablePublish &&
            showPublishInfo &&
            !isInTranslation;

        const isSelected = selectedNodeId === node.id;

        const showCheckIcon =
            (mode === 'change-parent' || mode === 'long-list-family-selection') &&
            selectedNodeId === node.id &&
            !isInTranslation;

        return (
            <PimSideBarElement
                ref={ref}
                stackProps={{
                    // Same horizontal extent as the search field and the selected-node title
                    // above (24px inset), with the title's inner padding and radius.
                    mx: 3,
                    paddingInline: 1,
                    borderRadius: 2,
                    cursor: userHasPermissions ? 'pointer' : 'default',
                    bgcolor: nodeListEdgeBackground(userHasPermissions, selectedNodeId, node.id),
                    // eslint-disable-next-line no-nested-ternary
                    color: node.disabled ? (isSelected ? 'grey.600' : 'grey.500') : 'text.primary',
                }}
                onClick={() => {
                    if (userHasPermissions) {
                        onEdgeSelected(edge, false);
                    }
                }}
            >
                <Stack width="100%">
                    {showBreadcrumb && (
                        <Breadcrumbs
                            separator={<KeyboardArrowRightIcon fontSize="small" />}
                            aria-label="breadcrumb"
                        >
                            {edge.node.breadcrumb
                                ?.slice(0, edge.node.breadcrumb.length - 1)
                                ?.map(b => (
                                    <Typography key={b.id} variant="caption">
                                        {b.listName}
                                    </Typography>
                                ))}
                        </Breadcrumbs>
                    )}
                    <Stack width="100%" direction="row" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={startIcon ? 1 : 0.5}>
                            {node.firstNodeStatus === 'notSynched' && (
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: 'hierarchy-notSynched.alert',
                                        defaultMessage: 'Non synched',
                                    })}
                                    placement="top"
                                >
                                    <WarningIcon fontSize="small" color="warning" />
                                </Tooltip>
                            )}

                            {startIcon}

                            <Stack minWidth={0}>
                                <Typography
                                    variant="body2"
                                    color={isSelected && !node.disabled ? 'primary' : 'inherit'}
                                >
                                    {isSelected ? <b>{node.listName}</b> : node.listName}
                                </Typography>
                                {!!subtitle && subtitle !== node.listName && (
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {subtitle}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>

                        <PimSideBarElementActions
                            actions={[
                                {
                                    type: 'custom',
                                    show: showContentTypeGroupsStatusesIcon,
                                    element: (
                                        <ContentTypeGroupStatuses
                                            status={node.contentTypeGroupStatus}
                                        />
                                    ),
                                },
                                {
                                    type: 'edit',
                                    show: isHierarchyViewMode && !isInTranslation,
                                    // The selected node is already open in the editor.
                                    disabled: isSelected,
                                },
                                {
                                    type: 'custom',
                                    show: showCheckIcon,
                                    element: <CheckIcon fontSize="small" />,
                                },
                                {
                                    type: 'children',
                                    show: showChildren,
                                    onClick: () => onEdgeSelected(edge, true),
                                },
                            ]}
                        />
                    </Stack>
                </Stack>
            </PimSideBarElement>
        );
    },
);
